<template>
  <div v-if="visibleIssues.length" class="sharer-billing-issues">
    <el-alert
      v-for="(issue, index) in visibleIssues"
      :key="issue.code + '-' + index"
      :type="alertType(issue)"
      :title="alertTitle(issue)"
      :description="alertDescription(issue)"
      :closable="false"
      show-icon
      role="alert" />
  </div>
</template>

<script>

// XOL-462 §6.6: fixed client-side title per code — a classification ("what
// kind of problem"), not a restatement of the API's sentence. Deliberately
// diverges from SharerFeeTimeline's title-less banner: that alert covers
// three codes with contradictory meanings, so one heading would be vague or
// wrong for one of them. Here each code gets its own alert, narrowly scoped.
// XOL-464: 'Billing has ended for this sharer' was withdrawn 2026-08-26. It
// read as a normal lifecycle end-state rather than a flagged error; it would
// render directly above the untouched open-ended notice on the Fee Timeline
// (which then read "the projection is capped at 240 months", and now reads
// "the projection runs to the end of the sharer rate's term" — XOL-466 deleted
// the cap) and flatly contradict it; and on the Fee Schedule it sits above a table of
// REAL, live in-term fees, since strict term there only suppresses the R3
// horizon stretch. The replacement classifies the condition instead of
// asserting an outcome, so it stays true in all three states at once.
// XOL-487 (D2-b): split by EDGE, not by flag — position_below_min_sharer owns
// the entire lower edge (strict and lenient rates alike); strict_position_out_of_range
// narrows to the upper edge only. Mutually exclusive.
var ISSUE_TITLE_MAP = {
  position_below_min_sharer: 'This sharer is below the rate\'s minimum position',
  strict_position_out_of_range: 'This sharer is above the rate\'s maximum position',
  strict_term_exceeded: 'This sharer is past the end of the rate\'s term'
}

// Deterministic order when both fire, independent of API array order.
// Position-violation leads because it invalidates EVERY month in the
// agreement — no rate applies at any point, so the schedule's tier bands
// carry `fee: null` throughout and the ledger dashes every row via
// `priced: false`. Term-violation invalidates only the months past the
// term; everything up to it billed correctly. Position is the broader
// failure, so it leads. A fixed order also keeps the two-banner screenshot
// reproducible. Below-Min leads Above-Max (XOL-487 D2-b): it fires on both
// lenient and strict rates, so it is the broader-applicable failure of the
// pair; the two are mutually exclusive so a fixed relative order between
// them is never actually exercised on one row, only in the sort's contract.
var ISSUE_ORDER = ['position_below_min_sharer', 'strict_position_out_of_range', 'strict_term_exceeded']

export default {
  name: 'sharer-billing-issues',

  props: {
    issues: {
      type: Array,
      default: function () { return [] }
    }
  },

  computed: {
    // The prose is the API's; a titled red panel with an empty body explains
    // nothing, so entries missing either a code or a detail are dropped.
    visibleIssues () {
      var list = Array.isArray(this.issues) ? this.issues : []
      var filtered = list.filter(function (issue) {
        return issue && issue.code && issue.detail
      })

      var known = []
      var unmapped = []
      filtered.forEach(function (issue) {
        if (ISSUE_ORDER.indexOf(issue.code) >= 0) {
          known.push(issue)
        } else {
          unmapped.push(issue)
        }
      })
      known.sort(function (a, b) {
        return ISSUE_ORDER.indexOf(a.code) - ISSUE_ORDER.indexOf(b.code)
      })

      return known.concat(unmapped)
    }
  },

  methods: {
    // Fail loud: this is the money path. Anything that is not explicitly
    // 'warning' — including 'error', missing, or an unrecognised value —
    // renders as 'error' rather than silently defaulting to a grey 'info'
    // notice for a billing failure.
    alertType (issue) {
      return issue.severity === 'warning' ? 'warning' : 'error'
    },

    alertTitle (issue) {
      var mapped = ISSUE_TITLE_MAP[issue.code]
      return mapped || issue.detail
    },

    alertDescription (issue) {
      return ISSUE_TITLE_MAP[issue.code] ? issue.detail : ''
    }
  }
}
</script>

<style scoped>
.sharer-billing-issues {
  margin-bottom: 12px;
}

.sharer-billing-issues ::v-deep .el-alert + .el-alert {
  margin-top: 8px;
}
</style>
